import React, { useEffect, useRef, useState } from 'react'
import { useComboList } from '../audit/multi-combo'
import { SEARCH_FIELD_PROPS } from '../component/search-field-props'
import { IconOrganization, IconUser } from '../icon/icon'
import { idQuery, lookupOrganizations, organizationNameOf, searchOrganizations } from './org-search-api'
import '../audit/audit.scss'
import './org-picker.scss'

// How long typing pauses before the picker asks content.
export const SEARCH_DELAY = 250
export const PAGE = 20

// The organization picker (owner 2026-10-13): one searchable organization,
// asked of content's organization search page by page -- never the whole
// list, which froze every page that loaded it (about 9,500 on prod, almost
// all personal).
//
// An ARIA combobox (the audit screens' look): the box shows the chosen
// organization; focusing it lists company organizations; typing searches
// them by name after a short pause; "12" or "#12" also finds that id, a
// personal one too. Personal accounts (each login's own organization, named
// by its email: almost all of prod's) are left out unless "Include personal
// accounts" is ticked (owner 2026-10-15: typing a company's name listed
// every one of its people), and then come after the companies under their
// own heading. "Show more" asks for the next page. ArrowUp/Down move, Enter
// picks, Escape or leaving the box puts the choice back. Each row shows the
// ID in its own column, since names repeat, and a building or a person in
// front of the name (owner 2026-10-15) says which kind it is at a glance --
// an id's match can be a personal account. org-picker.scss gives the picker
// its own colours, so it draws on the older form pages too.
//
// value: the chosen id as a string ('' for none). allOption: an extra first
// choice ({ value, label }, e.g. Every organization); extraOptions: more such
// choices after it (e.g. Platform -> API keys' "System keys"), each value a
// word no organization id can be. exclude: ids not to offer. A value not yet
// named (an id from the URL) is looked up by id.
export const OrgPicker = ({ id, label, value, onChange, allOption, extraOptions, exclude, placeholder = 'Search by name or ID', small, describedBy, disabled, required }) => {
  const [text, setText] = useState(null) // null: closed
  const [personal, setPersonal] = useState(false)
  const [rows, setRows] = useState([])
  const [next, setNext] = useState(null)
  const [status, setStatus] = useState('idle') // idle | loading | error
  const [active, setActive] = useState(0)
  const [, setNamed] = useState(0) // bumped when a lookup names the value
  const open = text !== null
  const seq = useRef(0)
  const input = useRef()

  const needle = (text || '').trim()
  const type = personal ? 'all' : 'company'
  // The choices that are not an organization: Every organization, then any others.
  const fixed = [allOption, ...(extraOptions || [])].filter(Boolean)
  const fixedChoice = fixed.find(o => o.value === value)

  // One page for the current text and toggle; a newer search always wins.
  useEffect(() => {
    if (!open) return undefined
    const mine = ++seq.current
    setStatus('loading')
    const timer = setTimeout(async () => {
      try {
        const asID = idQuery(needle)
        const [page, byID] = await Promise.all([searchOrganizations({ q: needle, type, limit: PAGE }), asID ? lookupOrganizations([asID]) : new Map()])
        if (seq.current !== mine) return
        const hit = asID && byID.get(asID)
        setRows(hit ? [hit, ...page.data.filter(o => Number(o.id) !== asID)] : page.data)
        setNext(page.next)
        setStatus('idle')
      } catch (err) {
        if (seq.current !== mine) return
        setRows([])
        setNext(null)
        setStatus('error')
      }
    }, SEARCH_DELAY)
    return () => clearTimeout(timer)
  }, [open, needle, type])

  // A chosen id this page hasn't named yet (from the URL): look it up.
  const isFixed = Boolean(fixedChoice)
  useEffect(() => {
    if (!value || isFixed || organizationNameOf(value) !== undefined) return undefined
    let live = true
    lookupOrganizations([value]).then(() => live && setNamed(n => n + 1), () => {})
    return () => {
      live = false
    }
  }, [value, isFixed])

  const more = async () => {
    const mine = ++seq.current
    setStatus('loading')
    try {
      const page = await searchOrganizations({ q: needle, type, limit: PAGE, cursor: next })
      if (seq.current !== mine) return
      setRows(r => [...r, ...page.data.filter(o => !r.some(x => x.id === o.id))])
      setNext(page.next)
      setStatus('idle')
    } catch (err) {
      if (seq.current === mine) setStatus('error')
    }
  }

  const chosen = (() => {
    if (fixedChoice) return fixedChoice.label
    if (value === '' || value === undefined || value === null) return ''
    return organizationNameOf(value) || `Organization ${value}`
  })()

  const out = new Set((exclude || []).map(Number))
  const shownFixed = open ? fixed.filter(o => !needle || o.label.toLowerCase().includes(needle.toLowerCase())) : []
  const offered = rows.filter(o => !out.has(Number(o.id))).map(o => ({ value: String(o.id), label: o.name, id: o.id, personal: Boolean(o.personal) }))
  // Companies, then personal accounts (an id's match leads its own group);
  // the list's order is the keyboard's order.
  const companies = offered.filter(o => !o.personal)
  const people = offered.filter(o => o.personal)
  const list = [...shownFixed.map(o => ({ value: o.value, label: o.label, all: true })), ...companies, ...people]
  const groups = people.length
    ? [
        { key: 'org', title: 'Organizations', rows: list.slice(0, list.length - people.length) },
        { key: 'people', title: 'Personal accounts', rows: people }
      ].filter(g => g.rows.length)
    : [{ key: 'org', rows: list }]
  const current = open ? list[Math.min(active, list.length - 1)] : undefined

  const listID = `${id}-list`
  const optID = i => `${id}-opt-${i}`
  const place = useComboList(open)

  const close = () => {
    setText(null)
    setActive(0)
  }
  const pick = row => {
    close()
    if (row && row.value !== value) onChange(row.value)
  }

  // A button inside the list: keep the box focused (and the list open).
  const keepFocus = e => e.preventDefault()
  const togglePersonal = () => {
    setPersonal(p => !p)
    setActive(0)
    input.current && input.current.focus()
  }

  const onKeyDown = e => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) {
        setText('')
        setActive(0)
      } else setActive(a => Math.min(list.length - 1, a + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(a => Math.max(0, a - 1))
    } else if (e.key === 'Enter') {
      // never submit the form the picker sits in
      e.preventDefault()
      if (open && current) pick(current)
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation()
      close()
    }
  }

  const hint =
    status === 'error'
      ? "Organizations couldn't be loaded."
      : status === 'loading' && !rows.length
        ? 'Loading organizations…'
        : !list.length
          ? personal || !needle
            ? 'No organizations match.'
            : 'No organizations match. To search personal accounts too, tick “Include personal accounts”.'
          : null

  return (
    <div className="au-combo au-orgpick" ref={place.anchor}>
      <input
        {...SEARCH_FIELD_PROPS}
        ref={input}
        id={id}
        name={id}
        className={`form-control${small ? ' form-control-sm' : ''}`}
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={listID}
        aria-autocomplete="list"
        aria-activedescendant={current ? optID(list.indexOf(current)) : undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        disabled={disabled}
        placeholder={open ? placeholder : chosen ? undefined : placeholder}
        value={open ? text : chosen}
        onChange={e => {
          setText(e.target.value)
          setActive(0)
        }}
        onFocus={() => {
          if (text === null) {
            setText('')
            setActive(0)
          }
        }}
        onBlur={close}
        onKeyDown={onKeyDown}
      />
      {open &&
        <div className="au-combo-list" ref={place.list} style={place.style} role="listbox" id={listID} aria-label={label} aria-busy={status === 'loading'}>
          {groups.map(g =>
            <div key={g.key} role={g.title ? 'group' : 'presentation'} aria-labelledby={g.title ? `${id}-grp-${g.key}` : undefined}>
              {g.title &&
                <div className="au-combo-grp" id={`${id}-grp-${g.key}`} role="presentation">
                  {g.title}
                </div>}
              {g.rows.map(r => {
                const i = list.indexOf(r)
                return (
                  <div
                    key={r.value}
                    id={optID(i)}
                    role="option"
                    aria-selected={r.value === value}
                    className={`au-opt${r === current ? ' au-active' : ''}`}
                    onMouseDown={e => {
                      e.preventDefault()
                      pick(r)
                    }}
                  >
                    <span className="au-opt-kind" aria-hidden="true" data-testid={r.all ? undefined : r.personal ? 'kind-person' : 'kind-organization'}>
                      {!r.all && (r.personal ? <IconUser /> : <IconOrganization />)}
                    </span>
                    <span className="au-opt-text">{r.label}</span>
                    {!r.all && <span className="au-opt-id sw-mono">#{r.id}</span>}
                  </div>
                )
              })}
            </div>)}
          {hint && <div className="au-combo-hint">{hint}</div>}
          <div className="au-combo-actions">
            {next &&
              <button type="button" className="btn btn-sm sw-btn" onMouseDown={keepFocus} onClick={more} disabled={status === 'loading'}>
                Show more
              </button>}
            <label className="au-orgpick-personal" onMouseDown={keepFocus}>
              <input type="checkbox" className="form-check-input" checked={personal} onChange={togglePersonal} />
              Include personal accounts
            </label>
          </div>
        </div>}
    </div>
  )
}
